import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Badge from '../../components/common/Badge';
import { bizuApi } from '../../api/bizu.api';
import { ArrowLeftIcon, CheckCircleIcon, XCircleIcon } from '@heroicons/react/24/outline';

export default function KienPlayedRecipesPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const response = await bizuApi.getKienPlayedRecipes(userId);
        setData(response.data);
      } catch (err) {
        console.error('Error fetching played recipes:', err);
        setError('Failed to load played recipes');
      } finally {
        setLoading(false);
      }
    };

    fetchRecipes();
  }, [userId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (error || !data) {
    return (
      <DashboardLayout>
        <div className="mb-6">
          <Button
            variant="secondary"
            onClick={() => navigate('/bizu/kien')}
            className="mb-4"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-2" />
            Back to Kien
          </Button>
        </div>
        <div className="text-center py-8 text-gray-500">
          {error || 'No recipes found'}
        </div>
      </DashboardLayout>
    );
  }

  const { user, recipes, totalRecipes, completedRecipes } = data;

  return (
    <DashboardLayout>
      <div className="mb-6">
        <Button
          variant="secondary"
          onClick={() => navigate('/bizu/kien')}
          className="mb-4"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to Kien
        </Button>
      </div>

      {/* User Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.fullName}'s Played Recipes</h1>
            <p className="text-gray-500">@{user.username}</p>
            {user.role && (
              <Badge variant={user.role === 'adult' ? 'info' : 'secondary'} className="mt-2">
                {user.role}
              </Badge>
            )}
          </div>
          <div className="text-right">
            <p className="text-sm text-gray-500">Recipes Completed</p>
            <p className="text-3xl font-bold text-gray-900">
              {completedRecipes} <span className="text-lg text-gray-400">/ {totalRecipes}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Recipes List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Played Recipes ({recipes.length})
          </h2>
        </div>

        {recipes.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No recipes played yet.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Recipe
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Level
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Ingredients Used
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recipes.map((recipe, index) => (
                  <tr key={recipe.recipe_id || index} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {recipe.recipe}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {recipe.completed ? (
                        <Badge variant="success">
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Completed
                        </Badge>
                      ) : (
                        <Badge variant="warning">
                          <XCircleIcon className="h-4 w-4 mr-1" />
                          In Progress
                        </Badge>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {recipe.level || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">
                      {recipe.ingredients?.length > 0
                        ? recipe.ingredients.join(', ')
                        : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
